/**
 * Motion Utilities
 * Helpers for scroll-reveal animations and number count-up effects.
 * CSS-first approach — no runtime animation libraries.
 */

"use client";

import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Returns staggered animation delay classes for child elements.
 * Use with the `.stagger-children` CSS class on the parent.
 */
export function staggeredEntryClasses(index: number): React.CSSProperties {
  return {
    animationDelay: `${index * 50}ms`,
  };
}

/**
 * Hook that adds a fade-in-up animation when an element scrolls into view.
 * Returns a ref to attach to the element and a boolean indicating visibility.
 *
 * Usage:
 *   const { ref, isVisible } = useScrollReveal();
 *   <div ref={ref} className={isVisible ? 'animate-fade-in-up' : 'opacity-0'}>
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(options?: {
  threshold?: number;
  triggerOnce?: boolean;
  rootMargin?: string;
}) {
  const { threshold = 0.1, triggerOnce = true, rootMargin = "0px 0px -40px 0px" } = options ?? {};
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) observer.unobserve(el);
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, triggerOnce, rootMargin]);

  return { ref, isVisible };
}

/**
 * Hook that animates a number from 0 to the target value.
 * Uses requestAnimationFrame for smooth 60fps counting.
 *
 * Usage:
 *   const displayValue = useCountUp(revenue, { duration: 1000, decimals: 0 });
 *   <span>{displayValue}</span>
 */
export function useCountUp(
  target: number,
  options?: {
    duration?: number;
    decimals?: number;
    prefix?: string;
    suffix?: string;
    enabled?: boolean;
  }
): string {
  const {
    duration = 800,
    decimals = 0,
    prefix = "",
    suffix = "",
    enabled = true,
  } = options ?? {};

  const [display, setDisplay] = useState(enabled ? "0" : target.toFixed(decimals));
  const rafRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);
  const prevTarget = useRef(target);

  const animate = useCallback(
    (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * target;

      setDisplay(current.toFixed(decimals));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setDisplay(target.toFixed(decimals));
      }
    },
    [target, duration, decimals]
  );

  useEffect(() => {
    if (!enabled) {
      setDisplay(target.toFixed(decimals));
      return;
    }

    // Respect reduced motion
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(target.toFixed(decimals));
      return;
    }

    // Reset animation when target changes
    if (prevTarget.current !== target) {
      startTimeRef.current = undefined;
      prevTarget.current = target;
    }

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, animate, enabled, decimals]);

  return `${prefix}${display}${suffix}`;
}

